import React, { useState, ChangeEvent, FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Building2,
  Globe,
  Briefcase,
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
} from "lucide-react";
import api from "../services/api";
import { setCurrentUser } from "../utils/auth";
import type { UserRole } from "../types/shared.types";
import "./OnboardingRegister.css";

interface RegistrationForm {
  companyName: string;
  country: string;
  industry: string;
  adminName: string;
  adminEmail: string;
  adminPassword: string;
  confirmPassword: string;
}

interface ApiError {
  response?: {
    data?: {
      error?: string;
      message?: string;
    };
  };
}

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "NL", name: "Netherlands" },
  { code: "BE", name: "Belgium" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "SE", name: "Sweden" },
  { code: "NO", name: "Norway" },
  { code: "DK", name: "Denmark" },
  { code: "FI", name: "Finland" },
  { code: "PL", name: "Poland" },
  { code: "CZ", name: "Czech Republic" },
  { code: "AT", name: "Austria" },
  { code: "CH", name: "Switzerland" },
  { code: "IE", name: "Ireland" },
  { code: "PT", name: "Portugal" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "JP", name: "Japan" },
  { code: "IN", name: "India" },
  { code: "BR", name: "Brazil" },
  { code: "MX", name: "Mexico" },
];

const INDUSTRIES = [
  "Technology",
  "Healthcare",
  "Finance & Banking",
  "Retail & E-commerce",
  "Manufacturing",
  "Education",
  "Consulting",
  "Staffing & Recruitment",
  "Legal",
  "Energy",
  "Real Estate",
  "Other",
];

const OnboardingRegister: React.FC = () => {
  const { t } = useTranslation("auth");
  const [formData, setFormData] = useState<RegistrationForm>({
    companyName: "",
    country: "",
    industry: "",
    adminName: "",
    adminEmail: "",
    adminPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const validateForm = (): boolean => {
    if (!formData.companyName.trim()) {
      setError(t("register.validation.companyNameRequired"));
      return false;
    }
    if (!formData.country) {
      setError(t("register.validation.countryRequired"));
      return false;
    }
    if (!formData.adminName.trim()) {
      setError(t("register.validation.nameRequired"));
      return false;
    }
    if (!formData.adminEmail.trim()) {
      setError(t("register.validation.emailRequired"));
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;
    if (!emailRegex.test(formData.adminEmail)) {
      setError(t("register.validation.emailInvalid"));
      return false;
    }
    if (formData.adminPassword.length < 8) {
      setError(t("register.validation.passwordTooShort"));
      return false;
    }
    if (formData.adminPassword !== formData.confirmPassword) {
      setError(t("register.validation.passwordMismatch"));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setError("");

    try {
      const response = (await api.onboarding.register({
        companyName: formData.companyName,
        country: formData.country,
        industry: formData.industry,
        adminName: formData.adminName,
        adminEmail: formData.adminEmail,
        adminPassword: formData.adminPassword,
      })) as {
        success: boolean;
        user?: { id: number; username: string; email: string; role: UserRole };
        error?: string;
        message?: string;
      };

      if (response.success && response.user) {
        setCurrentUser(response.user);
        navigate("/onboarding/welcome");
      } else {
        setError(
          response.error ||
            response.message ||
            t("register.errors.registrationFailed"),
        );
      }
    } catch (err) {
      const apiError = err as ApiError;
      setError(
        apiError.response?.data?.error ||
          apiError.response?.data?.message ||
          t("register.errors.registrationFailed"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="onboarding-register-container">
      <div className="onboarding-register-card">
        <div className="onboarding-register-header">
          <img
            src="/hirefocuz-logo.png"
            alt="HireFocuZ Logo"
            className="onboarding-logo"
          />
          <h1>{t("register.title")}</h1>
          <p>{t("register.subtitle")}</p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit} className="onboarding-register-form">
          <div className="form-section">
            <h3 className="form-section-title">
              <Building2 size={18} /> {t("register.sections.companyInfo")}
            </h3>

            <div className="form-group">
              <label className="form-label">
                {t("register.fields.companyName")} {t("register.required")}
              </label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                className="form-input"
                placeholder={t("register.fields.companyNamePlaceholder")}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  <Globe size={14} /> {t("register.fields.country")}{" "}
                  {t("register.required")}
                </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="form-input"
                  required
                >
                  <option value="">
                    {t("register.fields.countryPlaceholder")}
                  </option>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Briefcase size={14} /> {t("register.fields.industry")}
                </label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="">
                    {t("register.fields.industryPlaceholder")}
                  </option>
                  {INDUSTRIES.map((i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">
              <User size={18} /> {t("register.sections.adminAccount")}
            </h3>

            <div className="form-group">
              <label className="form-label">
                {t("register.fields.fullName")} {t("register.required")}
              </label>
              <input
                type="text"
                name="adminName"
                value={formData.adminName}
                onChange={handleChange}
                className="form-input"
                placeholder={t("register.fields.fullNamePlaceholder")}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <Mail size={14} /> {t("register.fields.email")}{" "}
                {t("register.required")}
              </label>
              <input
                type="email"
                name="adminEmail"
                value={formData.adminEmail}
                onChange={handleChange}
                className="form-input"
                placeholder={t("register.fields.emailPlaceholder")}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  <Lock size={14} /> {t("register.fields.password")}{" "}
                  {t("register.required")}
                </label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="adminPassword"
                    value={formData.adminPassword}
                    onChange={handleChange}
                    className="form-input"
                    placeholder={t("register.fields.passwordPlaceholder")}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Lock size={14} /> {t("register.fields.confirmPassword")}{" "}
                  {t("register.required")}
                </label>
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="form-input"
                    placeholder={t(
                      "register.fields.confirmPasswordPlaceholder",
                    )}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={16} />
                    ) : (
                      <Eye size={16} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="btn-primary w-100"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                {t("register.actions.creating")}
              </>
            ) : (
              t("register.actions.createWorkspace")
            )}
          </button>
        </form>

        <div className="onboarding-register-footer">
          <p>
            {t("register.footer.loginPrompt")}{" "}
            <Link to="/login" className="link-primary">
              {t("register.footer.loginLink")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingRegister;
